import {Component, ElementRef, HostListener, computed, effect, input, output, viewChild} from '@angular/core';
import {RoleGrantInvitation, roleLabel, scopeDescription} from './grant.models';

/**
 * Confirms re-sending an invitation by e-mail (#579), in a modal rather than inline in the row it was clicked
 * from.
 *
 * ### Why this is its own dialog and not a mode of {@link RevokeConfirmDialogComponent}
 *
 * The two share a shape and almost nothing else. That one asks about taking access away, and its load-bearing
 * sentence is „Auch bereits erteilte Rollen werden entzogen." — which is not merely wrong here but impossible:
 * an invitation that has produced grants is redeemed, and the server refuses a re-send of a redeemed
 * invitation outright (`E_GRANT_INVITE_ALREADY_REDEEMED`). Its heading, its confirm label and its red confirm
 * button all describe withdrawal. Sharing the component would have meant a second `kind` threaded through
 * every `computed` for the sake of reusing a `<div>`, and the price would have been paid in copy, which is the
 * one part of a confirmation prompt that has to be exactly right.
 *
 * ### What it has to say, and why the dialog exists at all
 *
 * A re-send is **not** a second copy of the same mail. The backend stores an unsalted SHA-256 of the code, so
 * there is no plaintext left to re-send: `RoleGrantService.ResendInvitation` revokes the invitation and issues
 * a replacement with a new code, in one save. The consequence is invisible from the button's label and
 * irreversible once done — anybody already holding the old code, including the recipient who is about to find
 * it in their inbox from last week, loses it. So the prompt names that first, and names the address the new
 * one is going to, because those are the two facts that decide whether pressing the button is right.
 *
 * ### Dismissible by backdrop and Escape
 *
 * Cancelling costs nothing (the invitation is untouched either way), so a stray click outside or an `Escape`
 * press is treated as an ordinary cancel — the same asymmetry {@link RevokeConfirmDialogComponent} documents
 * against {@link InviteCodeDialogComponent}, which blocks both because its secret is unrecoverable.
 */
@Component({
  selector: 'company-resend-confirm-dialog',
  standalone: true,
  template: `
    <!-- Backdrop click cancels: the safe direction, and nothing is lost by taking it. -->
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg
                  sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="resend-confirm-heading"
           aria-describedby="resend-confirm-body" (click)="$event.stopPropagation()">
        <div class="space-y-4 p-4 sm:p-6">

          <h2 id="resend-confirm-heading" class="text-xl font-bold tracking-tight text-gray-900">
            Einladung erneut senden?
          </h2>

          <div id="resend-confirm-body" class="space-y-3 text-sm text-gray-700">
            <p>{{ scope() }}</p>
            @if (recipient(); as address) {
              <p>Neue Einladung an: <span class="font-semibold text-gray-900">{{ address }}</span></p>
            }
            @if (roles().length) {
              <div class="flex flex-wrap gap-1">
                @for (role of roles(); track role) {
                  <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs
                               font-semibold text-gray-600">{{ label(role) }}</span>
                }
              </div>
            }
            <!-- The one thing the button label cannot say, on the surface that means "read this". -->
            <div class="rounded-2xl bg-amber-50 px-4 py-3 text-amber-800" role="alert">
              <p class="font-semibold">Der bisherige Code wird sofort ungültig.</p>
              <p class="mt-1">
                Wer ihn noch hat, kann ihn nicht mehr einlösen. Die neue Einladung gilt 72 Stunden.
              </p>
            </div>
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <!-- Cancel holds focus by default: a stray Enter press should land on the answer that changes
                 nothing, the same rule the revoke prompt follows. -->
            <button type="button" #cancelButton (click)="cancel()"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Abbrechen
            </button>
            <button type="button" (click)="confirm()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5
                           py-2.5 text-sm font-semibold text-white shadow-lg shadow-theme-primary-100
                           transition-all duration-200 hover:bg-theme-primary-700 hover:shadow-xl
                           active:scale-95 disabled:cursor-not-allowed disabled:opacity-30
                           disabled:grayscale">
              Ja, erneut senden
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class ResendConfirmDialogComponent {
  /** The invitation that is about to be replaced — set once; the parent mounts a fresh instance each time. */
  readonly invitation = input.required<RoleGrantInvitation>();

  /** Disables the confirm button while the call is in flight, mirroring the screen's own busy state. */
  readonly busy = input(false);

  /** Confirmed — the caller makes the call and closes the dialog on success. */
  readonly confirmed = output<void>();

  /** Cancel button, backdrop click, or Escape. All the same outcome: the invitation is untouched. */
  readonly cancelled = output<void>();

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  protected readonly label = roleLabel;

  /** The same tenant wording every other grant surface uses, so the row and the prompt name one thing once. */
  protected readonly scope = computed(() => scopeDescription(this.invitation()));

  /**
   * Where the replacement is going. Never absent in practice — the button is only offered for an invitation
   * with a bound address, because the server refuses a re-send without one — but rendered conditionally
   * anyway: a prompt that printed „Neue Einladung an:" followed by nothing would be worse than one that says
   * only what it knows.
   */
  protected readonly recipient = computed(() => this.invitation().boundEmail ?? null);

  /**
   * The roles being re-sent. Empty for an employee-link invitation, which confers no role at all, so the chip
   * strip disappears rather than rendering an empty row.
   */
  protected readonly roles = computed(() => this.invitation().roles ?? []);

  constructor() {
    // The caret starts on Cancel — see the buttons above.
    effect(() => {
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  protected confirm(): void {
    if (this.busy()) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
